import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";

const DIVISIONS = [
  {
    title: "Travel Division",
    body: "Airport chaos, sunbed politics, motel energy and all-inclusive confidence.",
  },
  {
    title: "Everyday Life",
    body: "Retail parks, takeaway runs, Aldi expeditions and petrol station elegance.",
  },
  {
    title: "Pub Division",
    body: "Beer gardens, quiz nights, darts confidence and suspicious carpets.",
  },
  {
    title: "Sports & Leisure",
    body: "Performancewear for people performing very little.",
  },
  {
    title: "Dad Life",
    body: "BBQ smoke, DIY optimism and carrying every shopping bag in one trip.",
  },
  {
    title: "Home Division",
    body: "Flat-packed furniture warfare and modern domestic collapse.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title="About"
        path="/about"
        description="Wet Cabbage is a quietly confident streetwear universe built around modern leisure behaviour. Six divisions documenting travel chaos, everyday life, pub culture, dad life, domestic disasters and aggressively average athletic ambition."
      />

      {/* MASTHEAD */}
      <section className="bg-[#F9F9F4]" data-testid="about-masthead">
        <div className="mx-auto max-w-[1100px] px-6 pb-10 pt-20 text-center md:px-10 md:pb-14 md:pt-28">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
            About &mdash; Wet Cabbage
          </div>
          <h1
            className="font-display mt-5 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
            data-testid="about-headline"
          >
            Modern life is
            <br />
            <span className="italic">unintentionally</span>
            <br />
            hilarious.
          </h1>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#F9F9F4] pb-12 md:pb-20" data-testid="about-manifesto">
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <div className="space-y-6 text-base leading-relaxed md:text-lg md:leading-[1.7]">
            <p>
              Wet Cabbage started with a simple observation: modern leisure
              behaviour deserves documenting properly.
            </p>
            <p>
              Airports. Pub gardens. BBQ incidents. Petrol station meal deals.
              Folding chairs. Sunbed diplomacy. Overpacked suitcases. Gym
              memberships powered entirely by confidence. Humanity keeps
              producing tiny masterpieces of chaos and somehow nobody turned
              any of it into premium personalised streetwear.
            </p>
            <p className="font-display text-xl font-black uppercase tracking-tight md:text-2xl">
              So we did.
            </p>
          </div>
        </div>
      </section>

      {/* DEFINITION */}
      <section
        className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24"
        data-testid="about-definition"
      >
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <div className="space-y-6 text-base leading-relaxed md:text-lg md:leading-[1.7]">
            <p>
              Wet Cabbage is a quietly confident streetwear universe inspired
              by holidays, pubs, everyday life and deeply committed leisure
              activities. Six divisions exploring travel chaos, suburban
              survival, pub culture, domestic disasters and emotionally
              athletic ambition.
            </p>
          </div>
        </div>
      </section>

      {/* PERSONALISATION */}
      <section
        className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24"
        data-testid="about-personalisation"
      >
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
            Personalised
          </div>
          <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
            Upload yourself into the universe.
          </h2>
          <div className="mt-6 space-y-6 text-base leading-relaxed md:text-lg md:leading-[1.7]">
            <p>
              Now you can upload yourself, your mates or your family directly
              into the world.
            </p>
            <p>
              Choose a design.
              <br />
              Send us a photograph.
              <br />
              We turn ordinary people into official Wet Cabbage legends using
              AI.
            </p>
            <p>
              Your dad becomes folklore.
              <br />
              Your mate becomes a documented incident.
              <br />
              Your holiday behaviour becomes wearable history.
            </p>
          </div>
        </div>
      </section>

      {/* INSPIRATION */}
      <section
        className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24"
        data-testid="about-inspiration"
      >
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <div className="space-y-6 text-base leading-relaxed md:text-lg md:leading-[1.7]">
            <p>
              Some brands are inspired by luxury yachts and minimalist
              architecture.
            </p>
            <p>
              We&rsquo;re inspired by a man discovering a €1 lager in Benidorm
              like he&rsquo;s uncovered ancient treasure.
            </p>
          </div>
        </div>
      </section>

      {/* DESIGN PHILOSOPHY + PULL QUOTE */}
      <section
        className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24"
        data-testid="about-philosophy"
      >
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <p className="text-base leading-relaxed md:text-lg md:leading-[1.7]">
            Everything we make is designed to feel wearable first and funny
            second. Premium personalised streetwear with observational humour
            stitched into the details. The kind of clothing that makes people
            stop and say:
          </p>

          <blockquote className="my-10 border-l-4 border-[#93C065] pl-6 md:my-14 md:pl-10">
            <p className="font-display text-2xl font-black uppercase leading-[1.05] tracking-tight text-[#1A3626] md:text-4xl lg:text-5xl">
              &ldquo;Wait&hellip; why is that exactly someone I know?&rdquo;
            </p>
          </blockquote>
        </div>
      </section>

      {/* DIVISIONS GRID */}
      <section
        className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24"
        data-testid="about-collections"
      >
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
              The universe
            </div>
            <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
              Our divisions document the
              <br className="hidden md:block" /> strange beauty of ordinary life.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-10 md:mt-16 md:grid-cols-2 md:gap-y-14">
            {DIVISIONS.map((c, i) => (
              <article
                key={c.title}
                className="border-t border-[#1A3626]/20 pt-6"
                data-testid={`about-collection-${i + 1}`}
              >
                <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display mt-3 text-2xl font-black uppercase tracking-tight md:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-[#1A3626]/85 md:text-lg">
                  {c.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* THE OPERATION */}
      <section
        className="bg-[#1A3626] py-16 text-[#F9F9F4] md:py-24"
        data-testid="about-operation"
      >
        <div className="mx-auto max-w-[860px] px-6 md:px-10">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#F9F9F4]/70 md:text-xs">
            Operation
          </div>
          <div className="mt-6 space-y-6 text-base leading-relaxed md:text-lg md:leading-[1.7]">
            <p>
              We&rsquo;re a small operation consisting of one curator, two
              vaguely related cousins and a dog named Mush who contributes
              absolutely nothing creatively but insists on supervising
              everything.
            </p>
            <p>
              Everything is made to order. No giant warehouses. No fashion
              industry waffle about &ldquo;elevated essentials&rdquo;. Just
              premium clothing, ridiculous ideas and products nobody needed
              until now.
            </p>
          </div>
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="bg-[#F9F9F4] py-20 md:py-28" data-testid="about-closing">
        <div className="mx-auto max-w-[900px] px-6 text-center md:px-10">
          <p className="font-display text-2xl font-black uppercase leading-[1.05] tracking-tight text-[#1A3626] sm:text-3xl md:text-4xl lg:text-5xl">
            Wet Cabbage isn&rsquo;t about perfection.
            <br />
            It&rsquo;s about recognising yourself
            <br className="hidden sm:block" /> somewhere in the chaos.
          </p>
        </div>
      </section>

      {/* UNDERDOGS / SPONSORSHIP */}
      <section
        className="border-t border-[#1A3626]/15 bg-[#F9F9F4] py-16 md:py-24"
        data-testid="about-underdogs"
      >
        <div className="mx-auto max-w-[860px] px-6 md:px-10">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
            Supporting underdogs
          </div>
          <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
            We like supporting underdogs.
          </h2>
          <div className="mt-6 space-y-6 text-base leading-relaxed text-[#1A3626] md:text-lg md:leading-[1.7]">
            <p>
              Unpredictable sports teams. Dangerously confident pub quiz groups.
              Five-a-side squads with enormous self-belief. BBQ teams one
              sausage away from glory.
            </p>
            <p>
              If your team, club or community deserves wildly unnecessary
              sponsorship, contact us.
            </p>
          </div>
          <div className="mt-8">
            <Link
              to="/sponsorship"
              className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] px-6 py-3 text-xs font-black uppercase tracking-[0.32em] text-[#1A3626] transition-all hover:-translate-y-0.5 hover:bg-[#1A3626] hover:text-[#F9F9F4] md:px-8 md:py-4 md:text-sm md:tracking-[0.36em]"
              data-testid="about-sponsorship-cta"
            >
              Pitch your underdog
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-[#F9F9F4] pb-24 pt-4 md:pb-32" data-testid="about-cta">
        <div className="mx-auto max-w-[1100px] px-6 text-center md:px-10">
          <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl">
            Buy a hoodie.
            <br />
            Join the universe.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/shop"
              className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#1A3626] px-7 py-4 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] md:px-9 md:py-5 md:text-sm md:tracking-[0.36em]"
              data-testid="about-shop-cta"
            >
              Shop the drop
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
